/**
 * Worauf ein Set beruht, als eine Zeile: „34 Belege: 18 Bewertungen,
 * 9 Forenbeiträge, 7 Suchbegriffe".
 *
 * WARUM: „Belegt" allein sagt nicht, ob drei Zitate oder dreißig dahinter
 * stehen und woher sie kommen. Die Zählung je Quelle ist die Recherche, die man
 * sieht, ohne dass ein Zitat die Seite verlässt. Keine Zahl hier wird
 * geschätzt: gezählt wird, was im Set steht.
 *
 * Zwei Eingänge, dieselbe Ausgabe: die Set-Liste liefert die Zählung fertig
 * (`evidence_by_type`, Migration 20260927160000), der Bericht zählt aus den
 * Belegen der eingefrorenen Personas selbst.
 */

import { t } from './sprache';

/**
 * Einzahl und Mehrzahl je bekannter Quellenart: die fünf aus der Vorlage in
 * research.ts plus die, die in echten Sets vorkommen (Stand 27.09.2026:
 * studie, fachbeitrag, branchenseite). Unbekannte erscheinen wörtlich.
 */
const ART: Record<string, [string, string]> = {
  review: ['Bewertung', 'Bewertungen'],
  studie: ['Studie', 'Studien'],
  fachbeitrag: ['Fachbeitrag', 'Fachbeiträge'],
  branchenseite: ['Branchenseite', 'Branchenseiten'],
  forum: ['Forenbeitrag', 'Forenbeiträge'],
  interview: ['Interview', 'Interviews'],
  ticket: ['Ticket', 'Tickets'],
  'search-term': ['Suchbegriff', 'Suchbegriffe'],
  'ohne-typ': ['ohne Angabe', 'ohne Angabe'],
};

const ART_EN: Record<string, [string, string]> = {
  review: ['review', 'reviews'],
  studie: ['study', 'studies'],
  fachbeitrag: ['expert article', 'expert articles'],
  branchenseite: ['industry site', 'industry sites'],
  forum: ['forum post', 'forum posts'],
  interview: ['interview', 'interviews'],
  ticket: ['ticket', 'tickets'],
  'search-term': ['search term', 'search terms'],
  'ohne-typ': ['unspecified', 'unspecified'],
};

export interface Beleglage {
  gesamt: number;
  /** Nach Anzahl absteigend, bei Gleichstand nach Name. */
  arten: Array<{ art: string; anzahl: number }>;
}

const ordne = (je: Record<string, number>): Beleglage['arten'] =>
  Object.entries(je)
    .filter(([, n]) => typeof n === 'number' && n > 0)
    .map(([art, anzahl]) => ({ art, anzahl }))
    .sort((a, b) => b.anzahl - a.anzahl || a.art.localeCompare(b.art));

/** Aus der fertigen Zählung der Set-Liste. `null`, wenn die Liste sie nicht trägt (Datenbank vor der Migration). */
export function beleglageAusZaehlung(gesamt: unknown, jeArt: unknown): Beleglage | null {
  if (typeof gesamt !== 'number' && typeof gesamt !== 'string') return null;
  if (!jeArt || typeof jeArt !== 'object' || Array.isArray(jeArt)) return null;
  const n = Number(gesamt);
  if (!Number.isFinite(n)) return null;
  return { gesamt: n, arten: ordne(jeArt as Record<string, number>) };
}

/** Aus den Belegen der Personas, wie sie im Bericht stehen. Gleiche Regel wie die Migration. */
export function beleglageAusPersonas(personas: Array<{ evidence?: unknown }>): Beleglage {
  const je: Record<string, number> = {};
  let gesamt = 0;
  for (const p of personas) {
    if (!Array.isArray(p?.evidence)) continue;
    for (const e of p.evidence) {
      const roh = typeof (e as any)?.typ === 'string' ? (e as any).typ.trim().toLowerCase() : '';
      const art = roh || 'ohne-typ';
      je[art] = (je[art] ?? 0) + 1;
      gesamt += 1;
    }
  }
  return { gesamt, arten: ordne(je) };
}

export function artText(art: string, anzahl: number): string {
  const namen = t(ART, ART_EN)[art];
  if (!namen) return `${anzahl} ${art}`;
  if (art === 'ohne-typ') return `${anzahl} ${namen[0]}`;
  return `${anzahl} ${anzahl === 1 ? namen[0] : namen[1]}`;
}

/**
 * Arten, in denen Käufer selbst sprechen (Stufe 1 und 2 der Rangfolge in
 * research.ts). Alles andere mit Namen, etwa Studien und Fachbeiträge, ist
 * Sekundärquelle und wird getrennt ausgewiesen: sonst sähe ein Set aus
 * Anbieterstudien genauso belegt aus wie eines aus Bewertungen.
 */
export const KUNDENSTIMMEN = new Set(['review', 'forum', 'interview', 'ticket', 'search-term']);

const liste = (arten: Beleglage['arten']) => arten.map(a => artText(a.art, a.anzahl)).join(', ');
const summe = (arten: Beleglage['arten']) => arten.reduce((n, a) => n + a.anzahl, 0);

/**
 * „34 Belege: 18 Bewertungen, 9 Forenbeiträge". Sind Sekundärquellen dabei:
 * „13 Belege: 5 Kundenstimmen (5 Forenbeiträge), 8 aus Sekundärquellen
 * (6 Studien, …)". Leer, wenn es keine Belege gibt.
 */
export function beleglageZeile(b: Beleglage | null): string {
  if (!b || b.gesamt <= 0) return '';
  const kopf = `${b.gesamt} ${b.gesamt === 1 ? t('Beleg', 'source') : t('Belege', 'sources')}`;
  // Nur eine Art ohne Angabe: dann sagt die Aufzählung nichts.
  if (b.arten.every(a => a.art === 'ohne-typ')) return kopf;
  const sekundaer = b.arten.filter(a => !KUNDENSTIMMEN.has(a.art) && a.art !== 'ohne-typ');
  if (sekundaer.length === 0) return `${kopf}: ${liste(b.arten)}`;
  const stimmen = b.arten.filter(a => KUNDENSTIMMEN.has(a.art));
  const ohne = b.arten.filter(a => a.art === 'ohne-typ');
  const k = summe(stimmen);
  const teile = [
    k > 0 ? `${k} ${k === 1 ? t('Kundenstimme', 'customer voice') : t('Kundenstimmen', 'customer voices')} (${liste(stimmen)})` : t('keine Kundenstimme', 'no customer voice'),
    `${summe(sekundaer)} ${t('aus Sekundärquellen', 'from secondary sources')} (${liste(sekundaer)})`,
    ...(ohne.length ? [liste(ohne)] : []),
  ];
  return `${kopf}: ${teile.join(', ')}`;
}
